import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SearchBar from "../../../Components/Inputs/SearchBar";
import { DownArrow, UpArrow } from "../../../Media/Icons";
import { convertToPST } from "../../../Helper/FormatDatetimes";
import { Export15Forms } from "../../../Components/Modals/PrintF15Forms";
import { RowClickF15Modal } from "../EditF15Modal";
import { usePaginatedData } from "../../../Hooks/usePaginatedData";
import instance from "../../../Interceptor/api_instance";
import DebouncedInput from "../../../Components/Inputs/DebouncedInput";
import { TableNavigation } from "../../../Components/Tables/Vehicles/TableNavigation";
import {
  TablePlaceHolder,
} from "../../../Components/LoadingAnimations/LoadingAnimations";
import { formatReadableDateTime } from "../../../Helper/FormatDatetimes";
import { SpinnerOverlay } from "../../../Components/LoadingAnimations/SpinnerOverlay";
import { createCsv } from "../../../Components/Utility/ExportToFile";
import { useApiMutation } from "../../../Hooks/useApiMutation";
import { useNavigate } from "react-router-dom";

const DEFAULT_URL = "/safety/ctpat-reports";

export const BacklogCTPAT = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { state, dispatch } = usePaginatedData(DEFAULT_URL);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedRows, setSelectedRows] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [modalRow, setModalRow] = useState(null);
  const [filters, setFilters] = useState({
    trailer_number: null,
    carrier: null,
    startDate: null,
    endDate: null,
    type: null,
    location: null,
  });

  // Use useApiMutation hook to create a mutation function for exporting F17 report
  const {
    mutate: exportF17,
    isLoading: exportLoading,
    error: exportError,
  } = useApiMutation(async (payload, { signal }) => {
    const res = await instance.post("/files/export-f17/", payload, { signal });
    return res;
  });

  const handleExportF17 = async () => {
    try {
      const response = await exportF17({ filters });
      if (response.status === 201) {
        navigate("/export-jobs");
      }
    } catch (error) {
      console.error("Error exporting F17 report:", error);
    }
  };

  const isAllSelected =
    selectedRows.length === state.data?.length && state.data?.length > 0;
  const isIndeterminate =
    selectedRows.length > 0 &&
    selectedRows.length < state.data?.results?.length;

  const headers = [
    t("yard-tables.table-columns.datetime"),
    t("yard-tables.table-columns.guard"),
    t("yard-tables.table-columns.eventType"),
    t("yard-tables.table-columns.driver"),
    t("yard-tables.table-columns.truck"),
    t("yard-tables.table-columns.trailer"),
    `${t("forms.type.vehicle.input-headers.origin")}/${t(
      "forms.type.vehicle.input-headers.destination"
    )}`,
    `${t("forms.type.vehicle.select.isLoaded.loaded")}/${t(
      "forms.type.vehicle.select.isLoaded.empty"
    )}`,
    t("forms.type.vehicle.input-headers.odometer"),
    t("yard-tables.table-columns.seal"),
  ];

  const buildUrl = (f) => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => {
      if (v !== null && v !== "" && v !== undefined) {
        if (Array.isArray(v)) v.forEach((item) => p.append(k, item));
        else p.append(k, v);
      }
    });
    const qs = p.toString();
    return qs ? `${DEFAULT_URL}?${qs}` : DEFAULT_URL; // avoid trailing "?"
  };

  const exportToCsv = async () => {
    try {
      dispatch({ type: "LOAD_PAGE", payload: true });

      if (!filters.startDate || !filters.endDate) {
        window.alert(t("errors.selectDates"));
        return;
      }
      // Check that startDate and endDate are maximum 7 days apart
      const startDate = filters.startDate ? new Date(filters.startDate) : null;
      const endDate = new Date(filters.endDate);
      const diffTime = Math.abs(endDate - startDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 7) {
        window.alert(t("errors.invalidDateRange"));
        return;
      }

      // Use current filters applied
      const queryString = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null) {
          // Exclude null values
          queryString.append(key, value);
        }
      });
      const url = `${DEFAULT_URL}?${queryString.toString()}&paginate=false`;
      const data = await instance.get(url);
      if (!data || data.length === 0) {
        window.alert(t("errors.noData"));
        return;
      }
      const parsedData = data.data.map((row) => ({
        [t(
          "yard-tables.table-columns.truck"
        )]: `${row.truck.carrier_name}-${row.truck.truck_number}`,
        [t("yard-tables.table-columns.trailer")]: row.trailer
          ? row.trailer.trailer_number
          : "N/A",
        [t("yard-tables.table-columns.eventType")]: row.check_in_dt
          ? t("yard-tables.table-columns.in")
          : t("yard-tables.table-columns.out"),
        [t("yard-tables.table-columns.datetime")]: formatReadableDateTime(
          row.check_in_dt ? row.check_in_dt : row.check_out_dt
        ),
        [t(
          "yard-tables.table-columns.driver"
        )]: `${row.visitor.first_name} ${row.visitor.last_name}`,
        [t("yard-tables.table-columns.guard")]: row.f15_truck.guard_name,
        [t("yard-tables.table-columns.content")]: row.trailer
          ? row.trailer.is_loaded
            ? t("forms.type.vehicle.placeholders.loaded")
            : t("forms.type.vehicle.placeholders.empty")
          : "N/A",
        [t("yard-tables.table-columns.seal")]: row.trailer
          ? row.check_in_dt
            ? row.trailer.seal_number
            : row.trailer.check_out_seal_number
          : "N/A",
        [t("forms.type.vehicle.input-headers.truck-plate")]:
          row.truck.truck_plate,
        [t("forms.type.vehicle.input-headers.trailer-plate")]: row.trailer
          ? row.trailer.license_plate
          : "N/A",
      }));
      createCsv(parsedData, `f15_reportes_${new Date().toISOString()}`);
    } catch (error) {
      console.log(error);
    } finally {
      dispatch({ type: "LOAD_PAGE", payload: false });
    }
  };

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        dispatch({ type: "FETCH_DATA" });
        const res = await instance.get(state.currentPage, {
          signal: ctrl.signal,
        });
        if (res.status === 200) {
          dispatch({
            type: "STORE_DATA",
            payload: res.data,
          });
        }
      } catch (e) {
        console.log(e);
      } finally {
        dispatch({ type: "LOAD_PAGE", payload: false });
      }
    })();
    return () => ctrl.abort();
  }, [state.currentPage]);

  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      // Select all rows
      setSelectedRows(state.data);
    } else {
      setSelectedRows([]);
    }
  };

  const selectCheckbox = (e, item) => {
    const isChecked = e.target.checked;

    setSelectedRows((prevSelectedRows) => {
      if (isChecked) {
        // Add the item to the selectedRows array
        return [...prevSelectedRows, item];
      } else {
        // Remove the item from the selectedRows array
        return prevSelectedRows.filter((row) => row.id !== item.id);
      }
    });
  };

  const handleOpenModal = (item) => {
    setModalRow(item);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalRow(null);
  };

  const handleFilter = (name, value) => {
    // derive the next filters snapshot
    const nextFilters = { ...filters };
    if (value === null || value === "" || value === undefined) {
      delete nextFilters[name]; // remove empty filters
    } else {
      nextFilters[name] = value;
    }

    // update state and navigate using the SAME snapshot
    setFilters(nextFilters);
    dispatch({
      type: "SET_CURRENT_PAGE",
      page: buildUrl(nextFilters),
      pageNum: 1,
    });
  };

  return (
    <>
      {state.pageLoading && <SpinnerOverlay />}
      {exportLoading && <SpinnerOverlay />}
      {exportError && <p className="error">{t("errors.exportError")}</p>}
      {state.error && <p className="error">{state.error}</p>}
      {isModalOpen && (
        <div className="page-mask page-mask-animation">
          <div className="modal-cont2">
            <RowClickF15Modal
              report={modalRow}
              close={closeModal}
              english={t}
            />
          </div>
        </div>
      )}
      <div className="search-filters-cont">
        <form id="filter-form">
          <div className="search-filters" style={{ alignItems: "center" }}>
            <label>
              {t("general.startDate")}:{" "}
              <input
                type="date"
                name="startDate"
                className="text-input-sm"
                value={filters.startDate}
                onChange={(e) => handleFilter("startDate", e.target.value)}
              />
            </label>
            <label>
              {t("general.endDate")}:{" "}
              <input
                type="date"
                value={filters.endDate}
                className="text-input-sm"
                name="endDate"
                onChange={(e) => handleFilter("endDate", e.target.value)}
              />
            </label>
            <select className="text-input-sm" onChange={(e) => handleFilter("location", e.target.value)}>
              <option value="">
                {t("yard-tables.table-columns.location")}
              </option>
              <option value={22}>Mexicali</option>
              <option value={6}>Queretaro</option>
              <option value={2}>Tijuana</option>
            </select>
            <select
              className="text-input-sm"
              value={filters.type}
              name="type"
              onChange={(e) => handleFilter("type", e.target.value)}
            >
              <option value="">
                {t("yard-tables.table-columns.event-type")}
              </option>
              <option value="true">{t("yard-tables.table-columns.in")}</option>
              <option value="false">
                {t("yard-tables.table-columns.out")}
              </option>
            </select>
            <DebouncedInput
              state={filters.carrier}
              setState={handleFilter}
              label={"carrier"}
              name={"carrier"}
            />
            <DebouncedInput
              state={filters.trailer_number}
              setState={handleFilter}
              label={"trailer"}
              name={"trailer_number"}
            />
            <div className="search-filters">
              <SearchBar />
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  className="all-btn success-btn"
                  type="button"
                  onClick={() => Export15Forms(selectedRows, setDownloading)}
                >
                  {t("buttons.print")}
                </button>
                <button
                  className="all-btn change-pwd-btn"
                  onClick={exportToCsv}
                  type="button"
                >
                  Export to CSV
                </button>
                  <button
                    className="all-btn cancel-btn"
                    type="button"
                    onClick={handleExportF17}
                  >
                    F17
                  </button>
              </div>
            </div>
          </div>
          <TableNavigation
            pageNum={state.currentPageNum}
            state={state}
            dispatch={dispatch}
          />
        </form>
      </div>
      <>
        {downloading || state.isLoading ? (
          <TablePlaceHolder />
        ) : (
          <div className="table-cont">
            <table id="forwardTable" className="yard-table">
              <thead>
                <tr className="table-header">
                  <th scope="col">
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={isAllSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = isIndeterminate;
                      }} // Handle the indeterminate state
                    />
                  </th>
                  {headers?.map((item, index) => (
                    <th key={index} scope="col">
                      <button className="col-header">
                        {item}
                        <div className="order-filter">
                          <UpArrow />
                          <DownArrow />
                        </div>
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {state.data?.map((item, index) => (
                  <tr
                    key={index}
                    className="data-rows row-hover"
                    data-testid={`row-${item.id}`}
                  >
                    <td data-label="check" key={index}>
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          selectCheckbox(e, item);
                        }}
                        checked={selectedRows.some(
                          (selected) => selected.id === item.id
                        )}
                      />
                    </td>
                    <td
                      data-label="datetime"
                      key={`${index}-datetime`}
                      className="gray"
                    >
                      {item.check_in_dt
                        ? convertToPST(item.check_in_dt)
                        : convertToPST(item.check_out_dt)}
                    </td>
                    <td
                      data-label="guard"
                      key={`${index}-guard`}
                      className="gray"
                    >
                      {item.user.first_name} {item.user.last_name}
                    </td>
                    <td
                      data-label="inorout"
                      key={`${index}-inout`}
                      className="gray"
                    >
                      {item.check_in_dt
                        ? t("yard-tables.table-columns.in")
                        : t("yard-tables.table-columns.out")}
                    </td>
                    <td
                      data-label="driver"
                      key={`${index}-driver`}
                      className="gray"
                    >
                      {item.visitor.first_name} {item.visitor.last_name}
                    </td>
                    <td
                      data-label="truck"
                      key={`${index}-truck`}
                      className="gray"
                      onClick={() => handleOpenModal(item)}
                    >
                      {item.truck.carrier_name}-{item.truck.truck_number}
                    </td>
                    <td
                      data-label="trailer"
                      key={`${index}-trailer`}
                      className="gray"
                    >
                      {item.trailer
                        ? item.trailer.trailer_number
                        : t("forms.type.vehicle.placeholders.none")}
                    </td>
                    <td
                      data-label="origin/destination"
                      key={`${index}-origindest`}
                      className="gray"
                    >
                      {item.check_in_dt
                        ? item.truck.origin
                        : item.truck.destination}
                    </td>
                    <td
                      data-label="content"
                      key={`${index}-content`}
                      className="gray"
                    >
                      {item.trailer
                        ? item.trailer.is_loaded
                          ? t("forms.type.vehicle.placeholders.loaded")
                          : t("forms.type.vehicle.placeholders.empty")
                        : "N/A"}
                    </td>
                    <td
                      data-label="odometer"
                      key={`${index}-odometer`}
                      className="gray"
                    >
                      {item.truck.odometer ? item.truck.odometer : "N/A"}
                    </td>
                    <td
                      data-label="seal"
                      key={`${index}-seal`}
                      className="gray"
                    >
                      {item.trailer
                        ? item.check_in_dt
                          ? item.trailer.seal_number
                            ? item.trailer.seal_number
                            : "N/A"
                          : item.trailer.check_out_seal_number
                          ? item.trailer.check_out_seal_number
                          : "N/A"
                        : "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    </>
  );
};
